/**
 * Presentational pieces for the Mentality overlay:
 *   - SuggestedCards: top N curated rules as "If…Then…" cards.
 *   - Card: single rule card, icon-coded by kind.
 *   - ChatBubble: user/bot turn with optional cards.
 *
 * Pure components — no side effects — so they're trivially testable
 * and reusable if the panel grows more sections.
 *
 * Pre-fetch loading rails: use <SkeletonOverlay /> from
 * core/ui/skeleton — the local OverlaySkeleton was consolidated there.
 */

import { ShieldAlert, Users as UsersIcon, Lightbulb } from 'lucide-react';
import type { MatchCard } from './adapters';
import type { ChatEntry } from './useMentalityChat';

export function SuggestedCards({ cards }: { cards: MatchCard[] }) {
    if (cards.length === 0) return null;
    return (
        <section className="mentality-overlay__suggested">
            <div className="mentality-overlay__section-label">Top rules right now</div>
            <ul className="mentality-overlay__cards">
                {cards.map((c, i) => <Card key={i} card={c} />)}
            </ul>
        </section>
    );
}

function Card({ card }: { card: MatchCard }) {
    const Icon = card.kind === 'risk' ? ShieldAlert
        : card.kind === 'profile' ? UsersIcon
        : Lightbulb;
    return (
        <li className={`mentality-card mentality-card--${card.kind}`}>
            <div className="mentality-card__head">
                <Icon size={12} aria-hidden="true" />
                <span className="mentality-card__kind">{card.kind}</span>
                {card.meta && <span className="mentality-card__meta">{card.meta}</span>}
            </div>
            <div className="mentality-card__body">
                <span className="mentality-card__if">If</span> <span>{card.title}</span>
                <span className="mentality-card__then">Then</span> <span>{card.then}</span>
            </div>
        </li>
    );
}

export function ChatBubble({ entry }: { entry: ChatEntry }) {
    return (
        <div className={`mentality-bubble mentality-bubble--${entry.from}`}>
            <p>{entry.text}</p>
            {entry.cards && entry.cards.length > 0 && (
                <ul className="mentality-overlay__cards">
                    {entry.cards.map((c, i) => <Card key={i} card={c} />)}
                </ul>
            )}
        </div>
    );
}
